import { lazy } from 'react';
import * as v from 'valibot';

import { DeepKeys } from '@tanstack/react-form';

import { formOpts } from '#form';

import {
	ClaimBody,
	ClaimValues,
	SingleClaim,
} from '@repo/api/claim/claims.types';
import { defaultTimeType } from '@repo/api/common.types';

export type Page = 'insured' | 'interruption' | 'policy' | 'review';

export const pageMap: { [key in Page]?: Page } = {
	insured: 'interruption',
	interruption: 'policy',
	policy: 'review',
};

export const pageTitles: { [key in Page]: string } = {
	insured: 'Insured Details',
	interruption: 'Interruption Details',
	policy: 'Policy Details',
	review: 'FNOL Summary',
};

export const fieldPages: { [key in DeepKeys<ClaimValues>]: Page } = {
	claimInsured: 'insured',
	'claimInsured.claimReferenceNumber': 'insured',
	'claimInsured.naicsCodeId': 'insured',
	'claimInsured.businessName': 'insured',
	'claimInsured.storeNumber': 'insured',
	'claimInsured.addressLine1': 'insured',
	'claimInsured.addressLine2': 'insured',
	'claimInsured.town': 'insured',
	'claimInsured.stateId': 'insured',
	'claimInsured.regionId': 'insured',
	'claimInsured.countryId': 'insured',
	'claimInsured.zipCode': 'insured',
	'claimInsured.name': 'insured',
	'claimInsured.email': 'insured',
	'claimInsured.phoneNumber': 'insured',
	'claim.hasAlternativeContact': 'insured',
	alternativeContact: 'insured',
	'alternativeContact.alternativeContactName': 'insured',
	'alternativeContact.alternativeContactEmail': 'insured',
	'alternativeContact.alternativeContactNumber': 'insured',
	'alternativeContact.isPrimaryContact': 'insured',
	'alternativeContact.relationshipToInsured': 'insured',
	claim: 'interruption',
	'claim.dateofLoss': 'interruption',
	'claim.incidentDescription': 'interruption',
	'claim.isCat': 'interruption',
	'claim.interruptionOnGoing': 'interruption',
	'claim.estimatedPeriodofIndemnity': 'interruption',
	'claim.estimatedPeriodofIndemnityDateType': 'interruption',
	'claim.calculation': 'interruption',
	'claim.catCodeId': 'interruption',
	'claim.causeOfLossCodeId': 'interruption',
	claimPolicy: 'policy',
	'claimPolicy.policyId': 'policy',
	'claimPolicy.policyNumber': 'policy',
	'claimPolicy.businessInterruptionLimit': 'policy',
	'claimPolicy.biLimit': 'policy',
	'claimPolicy.coInsurance': 'policy',
	'claimPolicy.percentage': 'policy',
	'claimPolicy.isMaximumLengthOfIndemnityUnlimited': 'policy',
	'claimPolicy.maximumLengthOfIndemnity': 'policy',
	'claimPolicy.waitingPeriod': 'policy',
	'claimPolicy.waitingPeriodDuration': 'policy',
	'claimPolicy.waitingPeriodDurationHours': 'policy',
	'claimPolicy.ordinaryPayrollInsured': 'policy',
	'claimPolicy.timeLimitApplied': 'policy',
	'claimPolicy.timeLimitNumber': 'policy',
	'claimPolicy.timeLimitDurationDays': 'policy',
	'claimPolicy.ordinaryPayrollAmounts': 'policy',
	'claimPolicy.dollarLimit': 'policy',
	'claimPolicy.isBusinessInterruptionSumInsured': 'policy',
	'claimPolicy.businessInterruptionSumInsured': 'policy',
	'claimPolicy.hasAverageClausePerPolicy': 'policy',
	'claimPolicy.isDeclarationLinkedPolicy': 'policy',
	'claimPolicy.organisationId': 'policy',
} as const;

export function isValidPage(page: string): page is Page {
	return ['insured', 'interruption', 'policy', 'review'].includes(page);
}

export const epoiWholeNumberMessage =
	'Estimated Period of Indemnity must be a positive whole number';

export const epoiDurationTypeChangeWarning =
	'Changing the EPOI will result in existing Sales Data and calculations being removed. The insured will need to provide new sales information.';

export const dateOfLossChangeWarning =
	'Updating the Date of Loss will require the insured to review and update sales data. The insured will receive an email with instructions to update the sales data. Please confirm the change to the Date of Loss is required.';

const normalizeDateOfLoss = (value: string | null | undefined) => {
	const trimmedValue = value?.trim();

	if (!trimmedValue) {
		return null;
	}

	return trimmedValue.split('T')[0] ?? null;
};

export const shouldConfirmDateOfLossChange = (
	previousDateOfLoss: string | null | undefined,
	currentDateOfLoss: string | null | undefined,
): boolean => {
	const previousDate = normalizeDateOfLoss(previousDateOfLoss);
	const currentDate = normalizeDateOfLoss(currentDateOfLoss);

	if (previousDate == null) {
		return false;
	}

	return previousDate !== currentDate;
};

export const shouldConfirmEpoiTypeChange = (
	previousType: number | null | undefined,
	currentType: number | null | undefined,
): boolean => {
	if (previousType == null) {
		return false;
	}

	return previousType !== (currentType ?? defaultTimeType);
};

export const epoiWaitingPeriodMessage =
	'Estimated Period of Indemnity must be greater than the policy waiting period';

export const MIN_TIER2_DAYS_EPOI = 1;
export const MAX_TIER2_DAYS_EPOI = 13;

export const epoiDaysRangeMessage = `Estimated Period of Indemnity must be between ${MIN_TIER2_DAYS_EPOI} and ${MAX_TIER2_DAYS_EPOI} when the period type is Days`;

export const MIN_TIER2_WEEKS_EPOI = 2;
export const MAX_TIER2_WEEKS_EPOI = 8;

export const epoiWeeksRangeMessage = `Estimated Period of Indemnity must be between ${MIN_TIER2_WEEKS_EPOI} and ${MAX_TIER2_WEEKS_EPOI} when the period type is Weeks`;

export const MAX_WAITING_PERIOD_HOURS = 72;
export const MAX_WAITING_PERIOD_DAYS = 7;

export const waitingPeriodPositiveWholeNumberError =
	'Waiting Period must be a positive integer';
export const waitingPeriodExceedsHoursError =
	'Waiting Period cannot exceed 72 hours';
export const waitingPeriodExceedsDaysError =
	'Waiting Period cannot exceed 7 days';

// Tier 2 claims are subject to an additional rule: the Estimated Period of
// Indemnity must exceed the policy waiting period (converted to a common
// unit of days). Tier 1 claims are not subject to this comparison.
export const epoiValid = (
	values: ClaimValues,
	tier2Id: string | undefined,
): boolean => {
	const estimatedPeriodOfIndemnity = values.claim.estimatedPeriodofIndemnity;

	if (estimatedPeriodOfIndemnity == null) {
		return true;
	}

	if (tier2Id == null || values.claim.calculation !== tier2Id) {
		return true;
	}

	const hasPolicyWaitingPeriod = values.claimPolicy?.waitingPeriod;
	const policyWaitingPeriod = values.claimPolicy?.waitingPeriodDuration;

	if (!hasPolicyWaitingPeriod || policyWaitingPeriod == null) {
		return true;
	}

	const policyWaitingPeriodIsHours =
		values.claimPolicy?.waitingPeriodDurationHours;
	const dateType =
		values.claim.estimatedPeriodofIndemnityDateType ?? defaultTimeType;

	const waitingPeriodInDays =
		policyWaitingPeriodIsHours ? policyWaitingPeriod / 24 : policyWaitingPeriod;

	const estimatedPeriodInDays =
		dateType === 0 ? estimatedPeriodOfIndemnity
		: dateType === 1 ? estimatedPeriodOfIndemnity * 7
		: estimatedPeriodOfIndemnity * 30;

	return estimatedPeriodInDays > waitingPeriodInDays;
};

export const getWaitingPeriodError = (
	period:
		| {
				waitingPeriod?: boolean | null;
				waitingPeriodDuration?: number | string | null;
				waitingPeriodDurationHours?: boolean | null;
		  }
		| undefined
		| null,
): string | undefined => {
	const { waitingPeriod, waitingPeriodDuration, waitingPeriodDurationHours } =
		period ?? {};

	if (
		!waitingPeriod ||
		waitingPeriodDuration == null ||
		waitingPeriodDuration === ''
	) {
		return undefined;
	}

	const normalizedDuration =
		typeof waitingPeriodDuration === 'number' ?
			waitingPeriodDuration
		:	Number(waitingPeriodDuration);

	if (normalizedDuration === 0) {
		return undefined;
	}

	if (
		Number.isNaN(normalizedDuration) ||
		!Number.isInteger(normalizedDuration) ||
		normalizedDuration < 0
	) {
		return waitingPeriodPositiveWholeNumberError;
	}

	const maxWaitingPeriod =
		waitingPeriodDurationHours === false ?
			MAX_WAITING_PERIOD_DAYS
		:	MAX_WAITING_PERIOD_HOURS;
	const errorMessage =
		waitingPeriodDurationHours === false ?
			waitingPeriodExceedsDaysError
		:	waitingPeriodExceedsHoursError;

	return normalizedDuration > maxWaitingPeriod ? errorMessage : undefined;
};

// Runs every EPOI validation rule and returns the first applicable error
// message, or undefined if the value is valid (EPOI itself is optional).
export const getEpoiError = (
	values: ClaimValues,
	tier2Id: string | undefined,
): string | undefined => {
	const estimatedPeriodOfIndemnity = values.claim.estimatedPeriodofIndemnity;

	if (estimatedPeriodOfIndemnity == null) {
		return undefined;
	}

	if (
		!Number.isInteger(estimatedPeriodOfIndemnity) ||
		estimatedPeriodOfIndemnity <= 0
	) {
		return epoiWholeNumberMessage;
	}

	const isTier2 = tier2Id != null && values.claim.calculation === tier2Id;
	const dateType =
		values.claim.estimatedPeriodofIndemnityDateType ?? defaultTimeType;

	if (
		isTier2 &&
		dateType === 0 &&
		(estimatedPeriodOfIndemnity < MIN_TIER2_DAYS_EPOI ||
			estimatedPeriodOfIndemnity > MAX_TIER2_DAYS_EPOI)
	) {
		return epoiDaysRangeMessage;
	}

	if (
		isTier2 &&
		dateType === 1 &&
		(estimatedPeriodOfIndemnity < MIN_TIER2_WEEKS_EPOI ||
			estimatedPeriodOfIndemnity > MAX_TIER2_WEEKS_EPOI)
	) {
		return epoiWeeksRangeMessage;
	}

	if (!epoiValid(values, tier2Id)) {
		return epoiWaitingPeriodMessage;
	}

	return undefined;
};

export function postData(
	values: ClaimValues,
	claimId: number,
	isDraft: boolean,
): ClaimBody {
	const claimPolicy = values.claimPolicy;

	return {
		isDraft,
		claimInsured: values.claimInsured,
		alternativeContact:
			values.claim.hasAlternativeContact ? values.alternativeContact : {},
		claim: {
			claimId,
			hasAlternativeContact: values.claim.hasAlternativeContact,

			dateofLoss:
				values.claim.dateofLoss ? values.claim.dateofLoss.split('T')[0] : null,
			incidentDescription: values.claim.incidentDescription,
			isCat: values.claim.isCat,
			interruptionOnGoing: values.claim.interruptionOnGoing,
			estimatedPeriodofIndemnity: values.claim.estimatedPeriodofIndemnity,
			estimatedPeriodofIndemnityDateType:
				values.claim.estimatedPeriodofIndemnityDateType ?? defaultTimeType,
			calculation: values.claim.calculation,
			catCodeId: values.claim.catCodeId,
			causeOfLossCodeId: values.claim.causeOfLossCodeId,
		},
		claimPolicy:
			claimPolicy == null ? null : (
				{
					...claimPolicy,
					policyId: claimPolicy.policyId === -1 ? null : claimPolicy.policyId,
					biLimit:
						claimPolicy.businessInterruptionLimit ? claimPolicy.biLimit : null,
					percentage: claimPolicy.coInsurance ? claimPolicy.percentage : null,
					maximumLengthOfIndemnity:
						!claimPolicy.isMaximumLengthOfIndemnityUnlimited ?
							claimPolicy.maximumLengthOfIndemnity
						:	null,
					waitingPeriodDuration:
						claimPolicy.waitingPeriod ?
							claimPolicy.waitingPeriodDuration
						:	null,
					waitingPeriodDurationHours:
						claimPolicy.waitingPeriod ?
							(claimPolicy.waitingPeriodDurationHours ?? true)
						:	null,
					timeLimitApplied:
						claimPolicy.ordinaryPayrollInsured ?
							claimPolicy.timeLimitApplied
						:	null,
					timeLimitNumber:
						claimPolicy.ordinaryPayrollInsured && claimPolicy.timeLimitApplied ?
							claimPolicy.timeLimitNumber
						:	null,
					timeLimitDurationDays:
						claimPolicy.ordinaryPayrollInsured && claimPolicy.timeLimitApplied ?
							(claimPolicy.timeLimitDurationDays ?? true)
						:	null,
					ordinaryPayrollAmounts:
						claimPolicy.ordinaryPayrollInsured ?
							claimPolicy.ordinaryPayrollAmounts
						:	null,
					dollarLimit:
						(
							claimPolicy.ordinaryPayrollInsured &&
							claimPolicy.ordinaryPayrollAmounts
						) ?
							claimPolicy.dollarLimit
						:	null,
					isDeclarationLinkedPolicy:
						claimPolicy.hasAverageClausePerPolicy ?
							claimPolicy.isDeclarationLinkedPolicy
						:	null,
					businessInterruptionSumInsured:
						claimPolicy.isBusinessInterruptionSumInsured ?
							claimPolicy.businessInterruptionSumInsured
						:	null,
				}
			),
	};
}

export const getValues = (data: SingleClaim): ClaimValues => {
	const alternativeContact = data.alternativeContact ?? {};

	let claimPolicy = data.claimPolicy;
	const policyIsEmpty = v.is(v.strictObject({}), claimPolicy);
	const policyIsSet = claimPolicy != null && !policyIsEmpty;
	if (claimPolicy != null && policyIsSet && claimPolicy.policyId == null) {
		claimPolicy = {
			...claimPolicy,
			policyId: -1,
		};
	}

	return {
		claimInsured: data.claimInsured,
		claim: {
			hasAlternativeContact: Object.values(alternativeContact).some(
				(v) => v != null,
			),
			dateofLoss: data.dateofLoss,
			isCat: data.isCat,
			interruptionOnGoing: data.interruptionOnGoing,
			incidentDescription: data.incidentDescription,
			estimatedPeriodofIndemnity: data.estimatedPeriodofIndemnity,
			estimatedPeriodofIndemnityDateType:
				data.estimatedPeriodofIndemnityDateType,
			calculation: data.calculationTypeId,
			catCodeId: data.catCodeId,
			causeOfLossCodeId: data.causeOfLossCodeId,
		},
		claimPolicy,
		alternativeContact: data.alternativeContact,
	};
};

export const claimOpts = formOpts<ClaimValues>();

export const FNOLForm = lazy(() => import('./FNOLForm'));
